import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { postConsultation } from "../lib/api";
import { INTAKES, EDUCATION_LEVELS } from "../data/static";
import Icon from "./Icon";
import { Dots } from "./motion/Reveal";

const ConsultationContext = createContext(() => {});
export const useConsultation = () => useContext(ConsultationContext);

const DESTINATIONS_LIST = ["USA", "UK", "Canada", "Australia", "New Zealand", "Germany", "Singapore", "Malaysia", "Mauritius", "Not sure yet"];

const EMPTY = {
  fullName: "", email: "", phone: "", currentEducation: "",
  preferredDestination: "", preferredCourse: "", preferredIntake: "", message: "",
};

const inputCls =
  "w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 transition-colors duration-200 focus:border-navy focus:outline-none";

function Field({ label, required, error, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
        {label} {required && <span className="text-dot-red">*</span>}
      </span>
      {children}
      {error && <span className="mt-1 block text-xs text-dot-red">{error}</span>}
    </label>
  );
}

export function ConsultationProvider({ children }) {
  const [open, setOpen] = useState(false);
  const openConsultation = useCallback(() => setOpen(true), []);

  return (
    <ConsultationContext.Provider value={openConsultation}>
      {children}
      <ConsultationModal open={open} onClose={() => setOpen(false)} />
    </ConsultationContext.Provider>
  );
}

function ConsultationModal({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setErrors({});
      setDone(false);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = "Please enter your full name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Please enter a valid email address";
    if (!form.phone.trim()) errs.phone = "Please enter your phone number";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please check the highlighted fields and try again.");
      return;
    }
    setSubmitting(true);
    try {
      await postConsultation(form);
      setDone(true);
      toast.success("Thank you. Our education advisor will contact you shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-label="Book a free consultation"
        >
          <div className="absolute inset-0 bg-navy-dark/70 backdrop-blur-sm" onClick={onClose} data-testid="consultation-modal-backdrop" />
          <motion.div
            className="relative max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-t-2xl bg-white p-6 shadow-2xl sm:rounded-2xl sm:p-8"
            initial={{ y: 48, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 48, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="consultation-modal"
          >
            <button
              onClick={onClose}
              className="absolute right-4 top-4 rounded-full p-2 text-slate-400 transition-colors duration-200 hover:bg-sand hover:text-navy"
              aria-label="Close consultation form"
              data-testid="consultation-modal-close"
            >
              <Icon name="X" className="h-5 w-5" />
            </button>

            {done ? (
              <div className="py-10 text-center" data-testid="consultation-success">
                <span className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-dot-green/10">
                  <Icon name="CheckCircle2" className="h-7 w-7 text-dot-green" />
                </span>
                <h3 className="font-display text-2xl font-semibold text-navy">Thank you.</h3>
                <p className="mt-2 text-slate-600">Our education advisor will contact you shortly.</p>
                <button
                  onClick={onClose}
                  className="mt-6 rounded-full bg-navy px-6 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-navy-light"
                  data-testid="consultation-success-close"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <Dots className="mb-3" />
                <h3 className="font-display text-2xl font-semibold text-navy sm:text-3xl">Book a Free Consultation</h3>
                <p className="mt-2 text-sm text-slate-600">
                  Tell us a little about yourself and an education advisor will be in touch.
                </p>
                <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
                  <Field label="Full Name" required error={errors.fullName}>
                    <input data-testid="consultation-fullname-input" className={inputCls} value={form.fullName} onChange={set("fullName")} placeholder="Your full name" />
                  </Field>
                  <Field label="Email" required error={errors.email}>
                    <input data-testid="consultation-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                  </Field>
                  <Field label="Phone" required error={errors.phone}>
                    <input data-testid="consultation-phone-input" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+00 000 000 000" />
                  </Field>
                  <Field label="Current Education">
                    <select data-testid="consultation-education-select" className={inputCls} value={form.currentEducation} onChange={set("currentEducation")}>
                      <option value="">Select level</option>
                      {EDUCATION_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </Field>
                  <Field label="Preferred Destination">
                    <select data-testid="consultation-destination-select" className={inputCls} value={form.preferredDestination} onChange={set("preferredDestination")}>
                      <option value="">Select destination</option>
                      {DESTINATIONS_LIST.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </Field>
                  <Field label="Preferred Course">
                    <input data-testid="consultation-course-input" className={inputCls} value={form.preferredCourse} onChange={set("preferredCourse")} placeholder="e.g. Computer Science" />
                  </Field>
                  <Field label="Preferred Intake">
                    <select data-testid="consultation-intake-select" className={inputCls} value={form.preferredIntake} onChange={set("preferredIntake")}>
                      <option value="">Select intake</option>
                      {INTAKES.map((i) => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Message">
                      <textarea data-testid="consultation-message-input" rows={3} className={inputCls} value={form.message} onChange={set("message")} placeholder="Anything else we should know?" />
                    </Field>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="sm:col-span-2 flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-navy-light disabled:opacity-60"
                    data-testid="consultation-submit-button"
                  >
                    {submitting && <Icon name="Loader2" className="h-4 w-4 animate-spin" />}
                    {submitting ? "Submitting..." : "Request Consultation"}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
